// The daily limit on automatic investigations, and the alarms waiting past it (#849).
//
// On the alert sources screen, because that is where alarms come in and where the Inbox
// opens an alarm the limit held. The count is today's, and today is the UTC day: a
// workspace has no time zone, so the reset time is said in UTC. The alarms waiting past
// the limit are listed here and answered in the Inbox, the one queue (#795, #808): started
// now, past the limit, or dismissed.

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import {
  capOf,
  severityLabel,
  utcTime,
  type AutomationRead,
  type HeldAlarm,
} from "../api/automation";
import { messageOf } from "../api/http";
import { Chip, Notice } from "./chrome";
import { INBOX_KINDS, filterPath } from "./inboxView";

export function AutomaticInvestigations({
  readOnly,
  onRead,
  onSaveCap,
}: {
  readOnly: boolean;
  onRead: () => Promise<AutomationRead>;
  onSaveCap: (dailyCap: number | null) => Promise<AutomationRead>;
}) {
  const [read, setRead] = useState<AutomationRead | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read once when the screen opens: the console hands down a new
  // `onRead` on every render, which is not a reason to read again.
  const reader = useRef(onRead);
  reader.current = onRead;
  const load = useCallback(async () => {
    try {
      setRead(await reader.current());
      setError(null);
    } catch (failure) {
      setError(messageOf(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="panel" aria-labelledby="automatic-investigations" data-testid="automatic-investigations">
      <div className="panel__head">
        <h2 id="automatic-investigations">Automatic investigations</h2>
        {read === null || read.started_today === null ? null : (
          <Chip tone={read.started_today >= capOf(read) ? "warn" : undefined}>
            {read.started_today} of {capOf(read)} started today
          </Chip>
        )}
      </div>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {read === null ? (
        error === null ? (
          <p className="muted panel__body" role="status">
            Reading the daily limit…
          </p>
        ) : null
      ) : (
        <>
          <DailyLimit read={read} readOnly={readOnly} onSave={async (cap) => setRead(await onSaveCap(cap))} />
          <Waiting waiting={read.waiting} />
        </>
      )}
    </section>
  );
}

function DailyLimit({
  read,
  readOnly,
  onSave,
}: {
  read: AutomationRead;
  readOnly: boolean;
  onSave: (dailyCap: number | null) => Promise<void>;
}) {
  const [draft, setDraft] = useState(String(capOf(read)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = async (value: number | null) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await onSave(value);
      if (value === null) setDraft(String(read.default_daily_cap));
      setSaved(true);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void save(Number(draft));
  };

  return (
    <form className="panel__body stack" onSubmit={submit} aria-busy={busy}>
      <p className="muted">
        When this many investigations have started on their own today, the next alarm waits here and in the Inbox for a
        person instead. The count resets at {utcTime(read.resets_at)}.
      </p>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <p className="field">
        <label htmlFor="daily-cap" className="field__label">
          Daily limit
        </label>
        <input
          id="daily-cap"
          className="input input--inline"
          type="number"
          min={0}
          max={read.max_daily_cap}
          required
          disabled={readOnly}
          value={draft}
          onChange={(event) => {
            setSaved(false);
            setDraft(event.target.value);
          }}
        />
        <small className="field__hint">
          {read.daily_cap === null
            ? `The default, ${read.default_daily_cap} a day.`
            : `This workspace's own. The default is ${read.default_daily_cap}.`}{" "}
          0 sends every alarm to a person.
        </small>
      </p>
      {readOnly ? null : (
        <div className="actions">
          <button type="submit" className="btn btn--primary btn--small" disabled={busy}>
            {busy ? "Saving…" : "Save the limit"}
          </button>
          {read.daily_cap === null ? null : (
            <button type="button" className="btn btn--small btn--quiet" disabled={busy} onClick={() => void save(null)}>
              Use the default
            </button>
          )}
          {saved ? <span role="status">Saved</span> : null}
        </div>
      )}
    </form>
  );
}

/** The alarms waiting past the limit: answered in the Inbox, the one queue (#795). */
function Waiting({ waiting }: { waiting: HeldAlarm[] }) {
  return (
    <div className="panel__body stack stack--tight" aria-label="Waiting for a person" role="region">
      <h3>Waiting for a person</h3>
      {waiting.length === 0 ? (
        <p className="muted">No alarm is waiting.</p>
      ) : (
        <>
          <ul className="list" aria-label="Held alarms">
            {waiting.map((held) => (
              <li key={held.id} className="stack stack--tight" data-testid="held-alarm">
                <strong className="break">{held.alarm}</strong>
                <small className="muted">
                  {severityLabel(held.severity)}
                  {held.source ? <> · from {held.source}</> : null}
                  {held.occurrences > 1 ? <> · fired {held.occurrences} times</> : null} · held{" "}
                  <time dateTime={held.held_at}>{utcTime(held.held_at)}</time>
                  {held.resolved ? <> · resolved while waiting</> : null}
                </small>
              </li>
            ))}
          </ul>
          <p>
            <a className="btn btn--small" href={filterPath(INBOX_KINDS.held_alert.filter)}>
              Answer {waiting.length === 1 ? "it" : "them"} in the Inbox
            </a>
          </p>
        </>
      )}
    </div>
  );
}
